import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import axios from 'axios';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import ModalPortal, { MODAL_Z } from '../../Components/CommonComponents/ModalPortal';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import DataTable from '../../Components/UI/DataTable';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

const STAFF_COLUMNS = [
  { key: 'name', label: 'Name', className: 'font-medium text-slate-900' },
  { key: 'position', label: 'Position' },
  { key: 'salary', label: 'Salary', align: 'right', render: (value) => `$${value}` },
  {
    key: 'joinDate',
    label: 'Join Date',
    render: (value) => (value ? format(new Date(value), 'MM/dd/yyyy') : 'N/A'),
  },
];

const API_URL = `${END_POINT}/attendance`;

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success',
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    salary: '',
    joinDate: new Date().toISOString().slice(0, 10),
    description: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchStaff = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: getJwtToken(),
        },
      });
      setStaff(response.data || []);
    } catch (error) {
      showNotification('Error fetching staff data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, formData, {
          headers: {
            Authorization: getJwtToken(),
          },
        });
        showNotification('Staff updated successfully', 'success');
      } else {
        await axios.post(
          API_URL,
          formData,
          {
            headers: {
              Authorization: getJwtToken(),
            },
          }
        );
        showNotification('Staff added successfully', 'success');
      }
      setOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      showNotification('Error saving staff data', 'error');
    }
  };

  const handleEdit = (staffMember) => {
    setFormData({
      name: staffMember.name || '',
      position: staffMember.position || '',
      salary: staffMember.salary || '',
      joinDate: staffMember.joinDate
        ? new Date(staffMember.joinDate).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      description: staffMember.description || '',
    });
    setEditingId(staffMember._id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await axios.delete(`${API_URL}/${id}`, {
          headers: {
            Authorization: getJwtToken(),
          },
        });
        showNotification('Staff deleted successfully', 'success');
        fetchStaff();
      } catch (error) {
        showNotification('Error deleting staff', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      salary: '',
      joinDate: new Date().toISOString().slice(0, 10),
      description: '',
    });
    setEditingId(null);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type,
    });
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 4000);
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  return (
    <RightWrapper>
      <div className="p-6 w-full">
        {/* Toast Notification */}
        {notification.show && (
          <div
            className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-lg shadow-lg text-white font-medium transition ${
              notification.type === 'error' ? 'bg-red-600' : 'bg-green-600'
            }`}
          >
            {notification.message}
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Staff Management</h1>
          <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={isLoading || !staff?.length}
            onClick={() =>
              exportToExcel(staff || [], 'Staff', [
                { key: 'name', label: 'Name' },
                { key: 'position', label: 'Position' },
                { key: 'salary', label: 'Salary' },
                {
                  key: 'joinDate',
                  label: 'Join Date',
                  format: (value) => (value ? format(new Date(value), 'dd/MM/yyyy') : ''),
                },
                { key: 'description', label: 'Description' },
              ])
            }
            className="flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export to Excel
          </button>
          <button
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition"
          >
            <span>+</span> Add Staff
          </button>
          </div>
        </div>

        {/* Staff Table */}
        <DataTable
          headers={STAFF_COLUMNS}
          data={staff || []}
          isLoading={isLoading}
          emptyMessage="No staff members yet."
          actions={(staffMember) => (
            <>
              <button
                type="button"
                onClick={() => handleEdit(staffMember)}
                className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDelete(staffMember._id)}
                className="rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50"
              >
                Delete
              </button>
            </>
          )}
        />

        {/* Add/Edit Staff Modal */}
        {open && (
          <ModalPortal isOpen={open} onClose={() => setOpen(false)}>
          <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4`}>
            <div role="dialog" aria-modal="true" className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  {editingId ? 'Edit Staff' : 'Add New Staff'}
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-gray-600 font-bold text-xl"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Position *</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Salary *</label>
                    <input
                      type="number"
                      name="salary"
                      value={formData.salary}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Join Date *</label>
                    <input
                      type="date"
                      name="joinDate"
                      value={formData.joinDate}
                      onChange={handleInputChange}
                      required
                      className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full border rounded-md p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow-sm"
                  >
                    {editingId ? 'Update' : 'Add'} Staff
                  </button>
                </div>
              </form>
            </div>
          </div></ModalPortal>
        )}
      </div>
    </RightWrapper>
  );
};

export default StaffManagement;